import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from "react";
import { useChargerHistoryQuery } from "../../services/query/eflow-charger-dashboard-api.query";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
import HistoryBreakdownPanel from "../history/HistoryBreakdownPanel";
import { useChargeHistoryStatusEntries } from "./useChargeHistoryStatusEntries";
const ChargeHistoryTotal = ({ assetId, fromDate, historyData, historyError, historyLoading, projectId, title, toDate }) => {
    // Get timezone offset from project metadata
    const { projectMetaData } = useProjectDetailsStore((state) => ({
        projectMetaData: state.projectMetaData
    }));
    const timezoneOffset = useMemo(() => {
        return projectMetaData?.offset || "UTC+00:00";
    }, [projectMetaData?.offset]);
    // Use today's date for both from and to if not provided
    const today = useMemo(() => {
        const now = new Date();
        // Set to start of day (00:00:00)
        now.setHours(0, 0, 0, 0);
        return now;
    }, []);
    const calculatedFromDate = fromDate || today;
    const calculatedToDate = toDate || today;
    // Memoize query parameters with timezone-aware formatted dates
    const historyQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(calculatedFromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(calculatedToDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            toTime: formattedToDate
        };
    }, [calculatedFromDate, calculatedToDate, timezoneOffset]);
    // Determine if component is using externally provided data
    const hasHistoryDataSource = historyError !== undefined || historyLoading !== undefined || historyData !== undefined;
    // Fetch charger history - skip internal query when using external data
    // Query will automatically refetch when historyQueryParams change
    const isQueryEnabled = !hasHistoryDataSource && !!assetId && !!projectId;
    const { data: queryData, isError: queryError, isLoading: queryLoading } = useChargerHistoryQuery(assetId || "", projectId || "", historyQueryParams, isQueryEnabled);
    const isError = hasHistoryDataSource ? !!historyError : queryError;
    const isLoading = hasHistoryDataSource ? !!historyLoading : queryLoading;
    // Show loading if assetId or projectId is not available yet
    const isWaitingForChargerId = !hasHistoryDataSource && (!assetId || !projectId);
    // Extract status data from API response
    // Note: TypeScript types define connectorStatusHistoryDetails as an array, but the API actually returns an object
    // Using type assertion to match actual API behavior
    const connectorDetails = (historyData?.connectorStatusHistoryDetails ||
        queryData?.connectorStatusHistoryDetails);
    const statuses = connectorDetails?.connectorStatusHistory;
    const statusEntries = useChargeHistoryStatusEntries(statuses);
    return (_jsx("div", { className: "flex flex-col flex-1 h-full", "data-test": "charge-history-total-panel", children: _jsx(HistoryBreakdownPanel, { "data-test": "charge-history-total", error: isError ? "Error loading data" : undefined, leftColumnHeader: "Status", loading: isWaitingForChargerId || isLoading, metrics: statusEntries, rightColumnHeader: "Power Used", title: title || "Charge History Total", unit: "hours" }) }));
};
export default ChargeHistoryTotal;
